import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface CollectionRow {
  collectionId: string
  loanAccountNo: string
  customerName: string | null
  /** FIELD_CASH | UPI_QR | UPI_LINK */
  channel: string
  /** CASH | UPI | CHEQUE | RTGS | NEFT | POS */
  mode: string
  /** CONFIRMED | PENDING | DISCREPANCY | FAILED | EXPIRED | CANCELLED | CREATED */
  status: string
  amount: number
  currency: string
  collectedAt: string | null
  empId: string | null
  agentName: string | null
  branchCode: string | null
  gateway: string | null
  gatewayReference: string | null
}

export interface Slice {
  key: string
  count: number
  amount: number
}

export interface CollectionSummary {
  confirmedAmount: number
  confirmedCount: number
  pendingAmount: number
  byStatus: Slice[]
  byChannel: Slice[]
  byMode: Slice[]
  byDay: Slice[]
  byAgent: Slice[]
}

// `| undefined` is explicit because the project runs with
// exactOptionalPropertyTypes: a filter the user has cleared is genuinely
// present-and-undefined, not absent.
export interface CollectionFilters {
  from?: string | undefined
  to?: string | undefined
  channel?: string | undefined
  mode?: string | undefined
  status?: string | undefined
  /** LMS posting: true = reconciled, false = not yet, absent = both. */
  reconciled?: boolean | undefined
}

/**
 * Both hooks take the same filters and hit the same scoped predicate
 * server-side, so the tiles and the table always describe the same slice.
 */
export function useCollections(filters: CollectionFilters, limit = 100) {
  return useQuery({
    queryKey: ['collections', 'list', filters, limit],
    queryFn: async () => {
      const res = await api.get<CollectionRow[]>('/v1/collections', {
        params: { ...filters, limit },
      })
      return res.data
    },
  })
}

export function useCollectionSummary(filters: CollectionFilters) {
  return useQuery({
    queryKey: ['collections', 'summary', filters],
    queryFn: async () => {
      const res = await api.get<CollectionSummary>('/v1/collections/summary', {
        params: filters,
      })
      return res.data
    },
  })
}

/**
 * The LMS repayment file — one row per payment.
 *
 * Distinct from `/v1/collections/export.xlsx`, which is the case-centric
 * export: that one answers "what is the state of this account", this one
 * answers "what money arrived, against which loan, and how do I match it in
 * the bank statement".
 *
 * Goes through axios rather than an anchor href because the endpoint is
 * authenticated — an `<a download>` sends no bearer token and returns 401.
 */
export async function downloadRepaymentsXlsx(filters: CollectionFilters): Promise<void> {
  const res = await api.get<Blob>('/v1/collections/repayments.xlsx', {
    params: filters,
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = `graviton-repayments-${filters.from ?? 'all'}-to-${filters.to ?? 'all'}.xlsx`
    a.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}
